import type { TutorialStep } from "../../constants/activationContent";

import * as React from "react";

import ButtonBadge from "@svg/chat/ButtonBadge";
import ChatMessage from "@svg/chat/ChatMessage";
import Cursor from "@svg/chat/Cursor";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { MousePointerClick } from "lucide-react";

function getStepIcon(stepId: string): React.ReactNode {
  switch (stepId) {
    case "tutorial-ask-chat":
      return <ChatMessage className="size-5" />;
    case "tutorial-reference-element":
      return <ButtonBadge className="h-4 w-auto" />;
    case "tutorial-fine-tune":
      return <Cursor className="size-4" />;
    default:
      return null;
  }
}

interface TutorialMediaProps {
  step: TutorialStep;
  onLoad: () => void;
  isLoaded: boolean;
}

function TutorialMedia({ step, onLoad, isLoaded }: TutorialMediaProps) {
  if (step.videoUrl) {
    return (
      <video
        src={step.videoUrl}
        className={twMerge(
          "size-full object-cover transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0",
        )}
        autoPlay
        muted
        loop
        playsInline
        onCanPlay={onLoad}
      />
    );
  }

  if (step.imageUrl) {
    return (
      <img
        src={step.imageUrl}
        alt={step.title}
        className={twMerge(
          "size-full object-cover transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0",
        )}
        onLoad={onLoad}
      />
    );
  }

  return null;
}

function EditButtonInline() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded border border-white/40 px-1 align-middle text-xs font-medium text-white/60">
      <MousePointerClick className="size-2.5" />
      Edit
    </span>
  );
}

function StepBodyWithEditButton({
  stepId,
  body,
}: {
  stepId: string;
  body: string;
}) {
  if (
    stepId === "tutorial-reference-element" ||
    stepId === "tutorial-fine-tune"
  ) {
    const parts = body.split("Edit");
    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <EditButtonInline />
          {parts[1]}
        </>
      );
    }
  }
  return <>{body}</>;
}

interface TutorialColumnProps {
  step: TutorialStep;
  isFirst: boolean;
  isLast: boolean;
}

export function TutorialColumn({ step, isFirst, isLast }: TutorialColumnProps) {
  const icon = getStepIcon(step.id);
  const [loadedStepId, setLoadedStepId] = React.useState<string | null>(null);
  const isLoaded = loadedStepId === step.id;

  const handleLoad = React.useCallback(() => {
    setLoadedStepId(step.id);
  }, [step.id]);

  return (
    <div className="flex flex-1 flex-col">
      {/* Media */}
      <div
        className={twMerge(
          "relative h-[240px] overflow-hidden",
          isFirst && "rounded-tl-xl",
          isLast && "rounded-tr-xl",
        )}
      >
        {!isLoaded && <Skeleton className="absolute inset-0" />}
        <TutorialMedia step={step} onLoad={handleLoad} isLoaded={isLoaded} />
      </div>

      {/* Label section */}
      <div className="flex flex-col gap-1 bg-white/10 p-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-editorial text-white">{step.title}</span>
        </div>
        <p className="text-sm leading-5 text-white/60">
          <StepBodyWithEditButton stepId={step.id} body={step.body} />
        </p>
      </div>
    </div>
  );
}
